// What every offline write shares (BL-2431, BL-2447; ADR 066 — Offline Mode § 3 and § 4): the
// signed-in scope's store and change queue, the refusal a write the device can't queue gets, and
// the commit that writes a write's queue entries and the lot it changes together.
import { formatLotNumber } from '@auctogon/core/isomorphic/lot-number';
import { isQueuedLotCreate, type ChangeQueueLabel, type QueuedEntryInput } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import type { LotDesiredState } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import type { OfflineDeltaLotDto } from '@auctogon/core-admin/schemas/responses/offline-sync';

import type { OfflineStore } from '../../gateways/native-device';
import type { OfflineRequest } from './create-offline-transport';
import { SIGNED_OUT } from './offline-answers';
import { noteOfflineLotsChanged } from './offline-screen-handlers';
import { OFFLINE_QUEUE_WAIT_MS, offlineWriteQueueFor, waitForOfflineWriteQueue, type OfflineWriteQueue } from './offline-write-queue';


/** What every write handler works with: the request, the scope's store and its change queue. */
export interface WriteContext {
  req: OfflineRequest;
  store: OfflineStore;
  queue: OfflineWriteQueue;
}

/**
 * A write the device can't queue: fail it as the network would, so the screen's error path renders.
 *
 * @throws always, a `TypeError('Failed to fetch')`.
 */
export function unqueueable(): never {
  throw new TypeError('Failed to fetch');
}

/**
 * Run a write with the active store and its change queue, or answer as a signed-out caller. A write
 * made before OfflineSyncProvider registers the scope's queue waits up to {@link OFFLINE_QUEUE_WAIT_MS}
 * for it (BUG-2147), rather than failing while the device store is still opening.
 *
 * @param request - the offline request.
 * @param body - the write, given the store and queue.
 * @returns the write's answer, or the signed-out answer.
 * @throws as a dropped connection when no queue registers for the scope in time (nothing would replay it).
 */
export async function withQueue<T>(
  request: OfflineRequest,
  body: (ctx: WriteContext) => Promise<T>,
): Promise<T | typeof SIGNED_OUT> {
  const store = await request.openStore();
  if (!store) return SIGNED_OUT;
  const { tenantId, userId } = store.scope;
  const queue = offlineWriteQueueFor(store.scope) ?? (await waitForOfflineWriteQueue({ tenantId, userId, timeoutMs: OFFLINE_QUEUE_WAIT_MS }));
  if (!queue) return unqueueable();
  return body({ req: request, store, queue });
}

/**
 * Commit a write's entries, the lot's desired state (BL-2681) and the lot record it changes, then
 * let the lists re-read.
 *
 * @param input - the store, queue, entries, the lot's state to put (if the write changes it) and the lot record to put.
 * @returns once committed.
 * @throws as a dropped connection when the queue refuses it (a reused id: unreachable with fresh ids).
 * @sideEffect Appends to the change queue and rewrites the lot record, in one device transaction.
 */
export async function commitLocalWrite(input: {
  store: OfflineStore;
  queue: OfflineWriteQueue;
  entries: QueuedEntryInput[];
  /** A lot made offline: its whole new state. */
  lotState?: LotDesiredState;
  /** A Save: changes the stored state inside the write's batch, so a send settling meanwhile is not overwritten. */
  lotStateChange?: (current: LotDesiredState | null) => LotDesiredState | null;
  lot: { key: string; value: OfflineDeltaLotDto; parentKey: string | null };
}): Promise<void> {
  const { store, queue, entries, lot, lotState, lotStateChange } = input;
  const result = await queue.queueLocalWrite({
    entries,
    lots: { put: [lot], delete: [] },
    ...(lotState || lotStateChange
      ? { lotStates: { put: lotState ? [lotState] : [], delete: [], ...(lotStateChange ? { update: [{ lotId: lot.key, change: lotStateChange }] } : {}) } }
      : {}),
  });
  if (!result.success) unqueueable();
  noteOfflineLotsChanged({ scope: store.scope, lot: lot.value });
}

/**
 * A lot made offline by a relist is still a queued entry; one made by a create is a lot state
 * (BL-2681) that the lot's other entries wait on by group instead. This names only the entry kind.
 *
 * @param store - the scope's store.
 * @param lotId - a lot's id.
 * @returns the relist entry a lot made offline is still waiting on, or null for any other lot.
 */
export async function pendingCreateOf(store: OfflineStore, lotId: string): Promise<string | null> {
  const entry = await store.outbox.get(lotId);
  return entry && isQueuedLotCreate(entry) ? entry.id : null;
}

/**
 * Whether a lot was made offline and has not synced: its create is a lot state (BL-2681), or it is
 * a relist's copy still queued. This is the "New lot" flag a queued change's label captures.
 *
 * @param store - the scope's store.
 * @param lotId - a lot's id.
 * @returns true for a lot the server has never seen.
 */
export async function isQueuedNewLot(store: OfflineStore, lotId: string): Promise<boolean> {
  if ((await pendingCreateOf(store, lotId)) !== null) return true;
  return (await store.lotStates.get(lotId))?.kind === 'create';
}

/**
 * How the queue names a lot, captured when its change is queued (ADR 066 — Offline Mode § 8).
 *
 * @param lot - the device's copy of the lot.
 * @param newLot - whether the lot is still unsynced (see {@link isQueuedNewLot}).
 * @returns the label.
 */
export function labelOf(lot: OfflineDeltaLotDto, newLot: boolean): ChangeQueueLabel {
  return { lotNumber: formatLotNumber(lot.detail), title: lot.detail.name, newLot };
}
